import { IconApps, IconCalendar, IconClipboardList, IconExternalLink, IconMenu2, IconPlus, IconRefresh, IconX } from '@tabler/icons-react';
import React, { useState } from 'react';
import { Navbar, Form, OverlayTrigger, Tooltip, Button, Dropdown, Nav, Offcanvas, Badge } from 'react-bootstrap';
import { startCase } from 'lodash';
import { getInitials } from '../../utils/getInitials';
import { getAvatarColor } from '../../utils/getAvatarColor';
import { useAuth } from '../../../auth/context/AuthProvider';
import PropertySelect from './PropertySelect';
import SearchReservations from './SearchReservations';
import { Link } from 'react-router';
import { NAV_LINKS } from '../../utils/navlink';
import SidebarLink from './SidebarLink';
import './sidebar.css';
import { NAVIGATION_URLS } from '../../utils/urls';
import CreateReservation from '../../../features/pms-reservation/components/CreateReservation';
import BookingDetailModalV2 from './BookingDetailModalV2';
import AddPaymentDialog from '../../../features/pms-reservation/components/add-payments/AddPaymentDialog';
import AddExtraDialog from '../../../features/pms-reservation/components/add-extra/AddExtraDialog';
import UpdateReservation from '../../../features/pms-reservation/components/update-reservation-v4/UpdateReservationV4';
import ChangeStatusDialog from '../../../features/pms-reservation/components/change-status/ChangeStatusDialog';
import ChangeCommentDialog from '../../../features/pms-reservation/components/comments/ChangeCommentDialog';
import SplitReservationModal from '../../../features/pms-calendar/components/calendar/SplitReservationModal';
import MergeReservationModal from '../../../features/pms-calendar/components/calendar/MergeReservationModal';
import SwitchReservationModal from '../../../features/pms-calendar/components/calendar/SwitchReservationModal';
import { useQueryClient } from '@tanstack/react-query';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import { useRedirectToBookingEngine } from '../../../services/sso/hooks/useRedirectToBookingEngine';
import { SkinData } from '../../../services/common/hooks/useSkinData';
import ReadOnlyGuard from '../ReadOnlyGuard';

interface SidebarProps {
    skinData?: SkinData;
    children: React.ReactNode;
}

// Maksimum badge role yang ditampilkan di menu akun; sisanya diringkas jadi "+N more".
const MAX_ROLE_BADGES = 3;

const Sidebar: React.FC<SidebarProps> = ({ children, skinData }) => {
    const { logout, user } = useAuth();
    const [show, setShow] = useState(false);
    const redirectToBookingEngine = useRedirectToBookingEngine();
    const defaultOpenIndex = NAV_LINKS.findIndex(link => link.name === 'Front Office');

    const [showCreate, setShowCreate] = useState<boolean>(false);
    const [showBookingDetail, setShowBookingDetail] = useState<boolean>(false);
    const [bookingId, setBookingId] = useState<string>('');
    const [showUpdate, setShowUpdate] = useState<boolean>(false);
    const [idWillUpdate, setIdWillUpdate] = useState<string>('');
    const [showUpdateStatus, setShowUpdateStatus] = useState<boolean>(false);
    const [idWillUpdateStatus, setIdWillUpdateStatus] = useState<string>('');
    const [showUpdateComment, setShowUpdateComment] = useState<boolean>(false);
    const [idWillUpdateComment, setIdWillUpdateComment] = useState<string>('');
    const [showAddPayment, setShowAddPayment] = useState<boolean>(false);
    const [idWillAddPayment, setIdWillAddPayment] = useState<string>('');
    const [showAddExtra, setShowAddExtra] = useState<boolean>(false);
    const [idWillAddExtra, setIdWillAddExtra] = useState<string>('');
    const [showSplitModal, setShowSplitModal] = useState<boolean>(false);
    const [idWillSplit, setIdWillSplit] = useState<string>('');
    const [showMergeModal, setShowMergeModal] = useState<boolean>(false);
    const [idWillMerge, setIdWillMerge] = useState<string>('');
    const [showSwitchModal, setShowSwitchModal] = useState<boolean>(false);
    const [idWillSwitch, setIdWillSwitch] = useState<string>('');
    const [openIndex, setOpenIndex] = useState<number | null>(defaultOpenIndex !== -1 ? defaultOpenIndex : null);

    const handleShow = () => setShow(true);
    const handleClose = () => setShow(false);

    const handleShowBookingDetail = (reservationId: string) => {
        setShowBookingDetail(true);
        setBookingId(reservationId);
    };
    const handleCloseBookingDetail = () => {
        setShowBookingDetail(false);
        setBookingId('');
    };

    const handleUpdate = (id: string) => {
        setIdWillUpdate(id);
        setShowUpdate(true);
        handleCloseBookingDetail();
    }

    const handleCloseUpdate = () => {
        setIdWillUpdate('');
        setShowUpdate(false);
    }

    const handleUpdateStatus = (id: string) => {
        setIdWillUpdateStatus(id);
        setShowUpdateStatus(true);
        handleCloseBookingDetail();
    }

    const handleCloseUpdateStatus = () => {
        handleShowBookingDetail(idWillUpdateStatus);
        setIdWillUpdateStatus('');
        setShowUpdateStatus(false);
    }

    const handleUpdateComment = (id: string) => {
        setIdWillUpdateComment(id);
        setShowUpdateComment(true);
        handleCloseBookingDetail();
    }

    const handleCloseUpdateComment = () => {
        handleShowBookingDetail(idWillUpdateComment);
        setIdWillUpdateComment('');
        setShowUpdateComment(false);
    }

    const handleAddPayment = (id: string) => {
        setIdWillAddPayment(id);
        setShowAddPayment(true);
        handleCloseBookingDetail();
    }

    const handleCloseAddPayment = () => {
        handleShowBookingDetail(idWillAddPayment);
        setIdWillAddPayment('');
        setShowAddPayment(false);
    }

    const handleAddExtra = (id: string) => {
        setIdWillAddExtra(id);
        setShowAddExtra(true);
        handleCloseBookingDetail();
    }

    const handleCloseAddExtra = () => {
        handleShowBookingDetail(idWillAddExtra);
        setIdWillAddExtra('');
        setShowAddExtra(false);
    }

    const handleSplit = (id: string) => {
        setIdWillSplit(id);
        setShowSplitModal(true);
        handleCloseBookingDetail();
    }

    const handleCloseSplit = () => {
        handleShowBookingDetail(idWillSplit);
        setIdWillSplit('');
        setShowSplitModal(false);
    }

    const handleMerge = (id: string) => {
        setIdWillMerge(id);
        setShowMergeModal(true);
        handleCloseBookingDetail();
    }

    const handleCloseMerge = () => {
        handleShowBookingDetail(idWillMerge);
        setIdWillMerge('');
        setShowMergeModal(false);
    }

    const handleSwitch = (id: string) => {
        setIdWillSwitch(id);
        setShowSwitchModal(true);
        handleCloseBookingDetail();
    }

    const handleCloseSwitch = () => {
        handleShowBookingDetail(idWillSwitch);
        setIdWillSwitch('');
        setShowSwitchModal(false);
    }

    const queryClient = useQueryClient();

    const handleRefreshData = () => {
        queryClient.refetchQueries();
        window.location.reload();
    }

    const handleToggle = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const handleAction = (action: string) => {
        if (action === 'ACTION_BOOKING_ENGINE_REDIRECT') {
            redirectToBookingEngine.mutate();
        }
    };

    return (
        <>
            <Navbar bg="white" variant="dark" className="px-3 d-flex justify-content-between align-items-center border-bottom-0" style={{ boxShadow: '0 1px 3px 0 rgba(0,0,0,.1), 0 1px 2px -1px rgba(0,0,0,.1)' }}>
                <button className="btn border-0 shadow-none ps-0 burger-nav" onClick={handleShow}>
                    <IconMenu2 size={24} className='text-primary' />
                </button>

                {user && <RequireAuth requiredRoles={['dev']} requiredPermissions={['change_property']} asComponent><PropertySelect user={user} /></RequireAuth>}

                <Form className="d-flex ms-3 me-1 align-items-center position-relative" style={{ flex: 1 }}>
                    <RequireAuth
                        requiredRoles={['dev']}
                        requiredPermissions={['booking_view']}
                        asComponent
                        fallback={<div className="form-control me-2 d-none d-sm-block border-0 bg-transparent shadow-none"></div>}>
                        <SearchReservations onDetailClick={handleShowBookingDetail} />
                    </RequireAuth>

                    {/* Desktop Buttons */}
                    <div className="d-none d-md-flex align-items-center">
                        {/* Create Reservation */}
                        <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_create']} asComponent>
                            <ReadOnlyGuard>
                                <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-create-reservation">Create Reservation</Tooltip>}>
                                    <Button variant="primary" className="rounded-circle mx-2 p-2" onClick={() => setShowCreate(true)}>
                                        <IconPlus size={18} />
                                    </Button>
                                </OverlayTrigger>
                            </ReadOnlyGuard>
                        </RequireAuth>

                        {/* Tools Dropdown */}
                        <Dropdown align="end" className="ms-2">
                            <Dropdown.Toggle variant="light" id="dropdown-tools" className="d-flex align-items-center border-0 bg-transparent text-primary shadow-none">
                                <IconApps size={18} className="me-2" />
                                <span className="fw-medium">Tools</span>
                            </Dropdown.Toggle>

                            <Dropdown.Menu className="shadow-sm border-0 mt-2">
                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_view']} asComponent>
                                    <Dropdown.Item as={Link} to={NAVIGATION_URLS.DASHBOARD_ADMIN_RESERVATION} className="d-flex align-items-center py-2">
                                        <IconClipboardList size={16} className="me-3 text-primary" />
                                        <span>Reservation Page</span>
                                    </Dropdown.Item>
                                </RequireAuth>

                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['calendar_view']} asComponent>
                                    <Dropdown.Item as={Link} to={NAVIGATION_URLS.DASHBOARD_ADMIN_RESERVATION_CALENDAR} className="d-flex align-items-center py-2">
                                        <IconCalendar size={16} className="me-3 text-primary" />
                                        <span>Calendar Page</span>
                                    </Dropdown.Item>
                                </RequireAuth>

                                <Dropdown.Divider />

                                <Dropdown.Item onClick={handleRefreshData} className="d-flex align-items-center py-2">
                                    <IconRefresh size={16} className="me-3 text-success" />
                                    <span>Refresh Data</span>
                                </Dropdown.Item>

                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_engine_access']} asComponent>
                                    <Dropdown.Item
                                        onClick={() => redirectToBookingEngine.mutate()}
                                        disabled={redirectToBookingEngine.isPending}
                                        className="d-flex align-items-center py-2"
                                    >
                                        <IconExternalLink size={16} className="me-3 text-info" />
                                        <span>{redirectToBookingEngine.isPending ? 'Redirecting...' : 'Open Booking Engine'}</span>
                                    </Dropdown.Item>
                                </RequireAuth>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>

                    {/* Mobile Dropdown */}
                    <div className="d-block d-md-none">
                        <Dropdown align="end">
                            <Dropdown.Toggle variant="primary" id="dropdown-mobile-actions">
                                Actions
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_create']} asComponent>
                                    <ReadOnlyGuard mode="hide">
                                        <Dropdown.Item onClick={() => setShowCreate(true)}>
                                            <IconPlus size={14} className="me-2" /> Create Reservation
                                        </Dropdown.Item>
                                    </ReadOnlyGuard>
                                </RequireAuth>

                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_view']} asComponent>
                                    <Dropdown.Item as={Link} to={NAVIGATION_URLS.DASHBOARD_ADMIN_RESERVATION}>
                                        <IconClipboardList size={14} className="me-2" /> Reservation List
                                    </Dropdown.Item>
                                </RequireAuth>

                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['calendar_view']} asComponent>
                                    <Dropdown.Item as={Link} to={NAVIGATION_URLS.DASHBOARD_ADMIN_RESERVATION_CALENDAR}>
                                        <IconCalendar size={14} className="me-2" /> Reservation Calendar
                                    </Dropdown.Item>
                                </RequireAuth>

                                <Dropdown.Item onClick={handleRefreshData}>
                                    <IconRefresh size={14} className="me-2" /> Refresh Data
                                </Dropdown.Item>

                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_engine_access']} asComponent>
                                    <Dropdown.Item
                                        onClick={() => redirectToBookingEngine.mutate()}
                                        disabled={redirectToBookingEngine.isPending}
                                    >
                                        <IconExternalLink size={14} className="me-2" />
                                        {redirectToBookingEngine.isPending ? 'Redirecting...' : 'Open Booking Engine'}
                                    </Dropdown.Item>
                                </RequireAuth>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </Form>

                <Nav className="d-flex align-items-center flex-nowrap">
                    {/* Left border (hide on mobile) */}
                    <div
                        className="d-none d-md-block"
                        style={{ borderLeft: '1px solid #ccc', height: '40px', margin: '0 10px' }}
                    ></div>

                    <Dropdown className="p-0 mx-1 mx-md-3">
                        <Dropdown.Toggle
                            variant="light"
                            id="dropdown-user"
                            className="d-flex align-items-center p-0 bg-transparent border-0"
                        >
                            {user && (
                                <span
                                    className="avatar avatar-sm rounded-circle me-2"
                                    style={{ width: '30px', height: '30px', minWidth: '30px', backgroundColor: getAvatarColor(user.id || user.email), color: '#fff' }}
                                    title={user.name}
                                >
                                    {getInitials(user.name) || '?'}
                                </span>
                            )}
                        </Dropdown.Toggle>

                        <Dropdown.Menu align="end" style={{ width: 260, maxWidth: 'calc(100vw - 24px)' }}>
                            {user && (
                                <>
                                    {/* Siapa yang sedang login. Nama/email bisa sangat panjang -> dipotong
                                        satu baris dengan ellipsis (teks lengkap di tooltip native `title`). */}
                                    <div className="px-3 py-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <span
                                                className="avatar rounded-circle flex-shrink-0"
                                                style={{ backgroundColor: getAvatarColor(user.id || user.email), color: '#fff' }}
                                                aria-hidden="true"
                                            >
                                                {getInitials(user.name) || '?'}
                                            </span>
                                            {/* minWidth: 0 supaya text-truncate bekerja di dalam flex row */}
                                            <div style={{ minWidth: 0 }}>
                                                <div className="fw-semibold text-truncate" title={user.name}>{user.name}</div>
                                                <div className="small text-muted text-truncate" title={user.email}>{user.email}</div>
                                            </div>
                                        </div>
                                        {user.role?.length > 0 && (
                                            <div className="d-flex flex-wrap gap-1 mt-2" title={user.role.join(', ')}>
                                                {user.role.slice(0, MAX_ROLE_BADGES).map((role) => (
                                                    <Badge
                                                        key={role}
                                                        bg="secondary"
                                                        className="text-white text-truncate"
                                                        style={{ maxWidth: '100%' }}
                                                    >
                                                        {startCase(role)}
                                                    </Badge>
                                                ))}
                                                {user.role.length > MAX_ROLE_BADGES && (
                                                    <Badge bg="light" className="text-secondary border">
                                                        +{user.role.length - MAX_ROLE_BADGES} more
                                                    </Badge>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <Dropdown.Divider />
                                </>
                            )}
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['my_profile']} asComponent>
                                <Dropdown.Item as={Link} to={NAVIGATION_URLS.DASHBOARD_ADMIN_PROFILE}>Profile</Dropdown.Item>
                            </RequireAuth>
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['update_profile']} asComponent>
                                <Dropdown.Item as={Link} to={NAVIGATION_URLS.DASHBOARD_ADMIN_CHANGE_PASSWORD}>Change Password</Dropdown.Item>
                            </RequireAuth>
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['my_property']} asComponent>
                                <Dropdown.Item as={Link} to={NAVIGATION_URLS.DASHBOARD_ADMIN_MY_PROPERTY}>My Property</Dropdown.Item>
                            </RequireAuth>
                            <Dropdown.Divider />
                            <Dropdown.Item as={Link} to="#" onClick={logout}>Logout</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </Nav>
            </Navbar>

            <Offcanvas show={show} onHide={handleClose} placement="start" className="sidebar-canvas">
                <Offcanvas.Header className='d-flex justify-content-between'>
                    <div className="d-flex lh-1 text-reset p-0">
                        {/* {user && <img src={'https://i.pravatar.cc/150?img=1'} alt={user.name} className="rounded-circle" style={{ width: '30px', height: '30px' }} />} */}
                        {skinData && skinData.logo_url && (
                            <img
                                src={skinData.logo_url}
                                alt="Company Logo"
                                style={{ height: '40px' }} // Sesuaikan ukuran sesuai kebutuhan
                            />
                        )}
                        {/* <div className="d-block ps-2">
                            <div>{user?.name}</div>
                            <div className="mt-1 small text-secondary">{user?.email}</div>
                        </div> */}
                    </div>
                    <a href='#'>
                        <IconX size={24} onClick={handleClose} className='text-primary' />
                    </a>
                </Offcanvas.Header>

                <Offcanvas.Body>
                    <Nav className="flex-column">
                        {NAV_LINKS.map((link, index) => (
                            <SidebarLink
                                key={index}
                                link={link}
                                index={index}
                                isOpen={openIndex === index}
                                onToggle={() => handleToggle(index)}
                                onAction={handleAction}
                            />
                        ))}
                    </Nav>
                </Offcanvas.Body>
            </Offcanvas>

            <main>
                {children}
            </main>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_create']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <CreateReservation show={showCreate} onClose={() => setShowCreate(false)} />
                </ReadOnlyGuard>
            </RequireAuth>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_view']} asComponent>
                <BookingDetailModalV2
                    bookingId={bookingId}
                    show={showBookingDetail}
                    onClose={handleCloseBookingDetail}
                    onUpdate={handleUpdate}
                    onUpdateStatus={handleUpdateStatus}
                    onUpdateComment={handleUpdateComment}
                    onAddPayment={handleAddPayment}
                    onAddExtra={handleAddExtra}
                    onSplit={handleSplit}
                    onMerge={handleMerge}
                    onSwitch={handleSwitch}
                />
            </RequireAuth>
            {showUpdate && idWillUpdate && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_update']} asComponent>
                    <UpdateReservation bookingId={idWillUpdate} show={showUpdate} onClose={handleCloseUpdate} />
                </RequireAuth>
            )}
            {showUpdateStatus && idWillUpdateStatus && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_update']} asComponent>
                    <ReadOnlyGuard mode="hide">
                        <ChangeStatusDialog show={showUpdateStatus} onClose={handleCloseUpdateStatus} reservationId={idWillUpdateStatus} />
                    </ReadOnlyGuard>
                </RequireAuth>
            )}
            {showUpdateComment && idWillUpdateComment && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_update']} asComponent>
                    <ReadOnlyGuard mode="hide">
                        <ChangeCommentDialog show={showUpdateComment} onClose={handleCloseUpdateComment} reservationId={idWillUpdateComment} />
                    </ReadOnlyGuard>
                </RequireAuth>
            )}
            {showAddPayment && idWillAddPayment && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_update']} asComponent>
                    <ReadOnlyGuard mode="hide">
                        <AddPaymentDialog show={showAddPayment} onClose={handleCloseAddPayment} reservationId={idWillAddPayment} />
                    </ReadOnlyGuard>
                </RequireAuth>
            )}
            {showAddExtra && idWillAddExtra && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_extra']} asComponent>
                    <ReadOnlyGuard mode="hide">
                        <AddExtraDialog show={showAddExtra} onClose={handleCloseAddExtra} reservationId={idWillAddExtra} />
                    </ReadOnlyGuard>
                </RequireAuth>
            )}
            {showSplitModal && idWillSplit && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_update']} asComponent>
                    <ReadOnlyGuard mode="hide">
                        <SplitReservationModal show={showSplitModal} onClose={handleCloseSplit} bookingId={idWillSplit} />
                    </ReadOnlyGuard>
                </RequireAuth>
            )}
            {showMergeModal && idWillMerge && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_update']} asComponent>
                    <ReadOnlyGuard mode="hide">
                        <MergeReservationModal show={showMergeModal} onClose={handleCloseMerge} bookingId={idWillMerge} />
                    </ReadOnlyGuard>
                </RequireAuth>
            )}
            {showSwitchModal && idWillSwitch && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['booking_update']} asComponent>
                    <ReadOnlyGuard mode="hide">
                        <SwitchReservationModal show={showSwitchModal} onClose={handleCloseSwitch} bookingId={idWillSwitch} />
                    </ReadOnlyGuard>
                </RequireAuth>
            )}
        </>
    );
};

export default Sidebar;